import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { SessionRestore } from '../../components/quiz/SessionRestore';
import { abandonQuizSession } from './quizSessionRuntime';
import {
  prepareQuizRuntimeLaunch,
  type PrepareQuizRuntimeLaunchResult,
} from './prepareQuizRuntimeLaunch';
import { openQuizRuntimeHref } from './quizRuntimeNavigation';

type PromptState = Extract<PrepareQuizRuntimeLaunchResult, { kind: 'prompt' }>;

type QuizRuntimeLaunchContextValue = {
  /** Pre-open gate: may show SessionRestore on the current page, then open /quiz. */
  launchQuizRuntime: (href: string) => Promise<void>;
};

const QuizRuntimeLaunchContext = createContext<QuizRuntimeLaunchContextValue | null>(null);

export function QuizRuntimeLaunchProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PromptState | null>(null);
  const busyRef = useRef(false);
  const pendingRef = useRef<PromptState | null>(null);

  const launchQuizRuntime = useCallback(async (href: string) => {
    if (busyRef.current || pendingRef.current) return;
    busyRef.current = true;
    try {
      const decision = await prepareQuizRuntimeLaunch(href);
      if (decision.kind === 'open') {
        openQuizRuntimeHref(decision.href);
        return;
      }
      pendingRef.current = decision;
      setPending(decision);
    } finally {
      busyRef.current = false;
    }
  }, []);

  const handleContinue = useCallback(() => {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    setPending(null);
    openQuizRuntimeHref(current.continueHref);
  }, []);

  const handleDiscard = useCallback(() => {
    const current = pendingRef.current;
    if (!current) return;
    pendingRef.current = null;
    setPending(null);
    void (async () => {
      await abandonQuizSession(current.session.id).catch(() => {});
      openQuizRuntimeHref(current.freshHref);
    })();
  }, []);

  const value = useMemo(() => ({ launchQuizRuntime }), [launchQuizRuntime]);

  return (
    <QuizRuntimeLaunchContext.Provider value={value}>
      {children}
      {pending ? (
        <SessionRestore
          session={pending.session}
          onContinue={handleContinue}
          onDiscard={handleDiscard}
        />
      ) : null}
    </QuizRuntimeLaunchContext.Provider>
  );
}

export function useQuizRuntimeLaunch(): QuizRuntimeLaunchContextValue {
  const ctx = useContext(QuizRuntimeLaunchContext);
  if (!ctx) {
    throw new Error('useQuizRuntimeLaunch must be used within QuizRuntimeLaunchProvider');
  }
  return ctx;
}

/** Safe for optional trees — falls back to immediate open (no pre-prompt). */
export function useOptionalQuizRuntimeLaunch(): QuizRuntimeLaunchContextValue {
  const ctx = useContext(QuizRuntimeLaunchContext);
  return (
    ctx ?? {
      launchQuizRuntime: async (href: string) => {
        openQuizRuntimeHref(href);
      },
    }
  );
}
